import { ref, computed, watch } from 'vue';

import { defineStore } from 'pinia';

import { getMarketplaceUid } from '@/helpers/marketplaceUnion';
import { isToastSuppressedPath } from '@/helpers/toastSuppression';
import { businessProfileIdFromToken } from '@/services/auth/jwtExpiry';
import {
  notificationSettingsService,
  notificationCacheService,
  defaultPreferences,
  getNotificationCategory,
  getNotificationTab,
} from '@/services/notificationSettingsService';
import { tokenService } from '@/services/tokenService.js';
import { webSocketService, CONNECTION_STATES } from '@/services/websocket';
import { useAccountStore } from '@/stores/account';
import { useAuthStore } from '@/stores/auth';

export const useNotificationStore = defineStore('notifications', () => {
  // State
  const notifications = ref([]);
  const connectionState = ref(CONNECTION_STATES.DISCONNECTED);
  const preferences = ref({ ...defaultPreferences });
  const isDrawerOpen = ref(false);
  const hasNewNotification = ref(false); // For bell animation

  // Computed
  const unreadCount = computed(() => {
    // Global (bell + drawer header) count excludes system notifications — they're
    // noise. Their unread count still shows on the System tab in the drawer.
    return notifications.value.filter((n) => !n.read && getNotificationTab(n) !== 'system').length;
  });

  const unreadByCategory = computed(() => {
    const counts = {
      sync: 0,
      reports: 0,
      campaigns: 0,
      system: 0,
    };

    notifications.value
      .filter((n) => !n.read)
      .forEach((n) => {
        const category = getNotificationCategory(n.type);
        if (counts[category] !== undefined) {
          counts[category]++;
        }
      });

    return counts;
  });

  const sortedNotifications = computed(() => {
    return [...notifications.value].sort((a, b) => {
      // Unread first, then by timestamp
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
  });

  // Dedup: only fetch server preferences once per app token. Reset on failure to retry.
  let lastLoadedToken = null;

  // Actions
  async function loadPreferences() {
    // Needs an app token. Until one exists, use local prefs (avoids a pre-auth 401);
    // App.vue re-invokes this via an accessToken watcher once the token is ready.
    const token = useAuthStore().accessToken;
    if (!token) {
      preferences.value = notificationSettingsService.getPreferences();
      return;
    }
    if (token === lastLoadedToken) return;
    lastLoadedToken = token;
    try {
      preferences.value = await notificationSettingsService.fetchSettings();
    } catch (error) {
      lastLoadedToken = null; // allow retry on the next trigger
      console.error('[NotificationStore] Error loading preferences:', error);
      // Fallback to local
      preferences.value = notificationSettingsService.getPreferences();
    }
  }

  async function savePreferences() {
    try {
      await notificationSettingsService.updateSettings(preferences.value);
    } catch (error) {
      console.error('[NotificationStore] Error saving preferences:', error);
    }
  }

  function updatePreferences(newPrefs) {
    preferences.value = {
      ...preferences.value,
      ...newPrefs,
      types: {
        ...preferences.value.types,
        ...(newPrefs.types || {}),
      },
    };
    savePreferences();
  }

  async function resetPreferences() {
    preferences.value = await notificationSettingsService.resetPreferences();
  }

  function loadCachedNotifications() {
    notifications.value = notificationCacheService.getNotifications();
  }

  let cacheTimeout = null;
  function saveToCacheThrottled() {
    // Debounce cache saves
    if (cacheTimeout) {
      clearTimeout(cacheTimeout);
    }
    cacheTimeout = setTimeout(() => {
      notificationCacheService.saveNotifications(notifications.value);
    }, 500);
  }

  function addNotification(notification) {
    // Report snapshots and mutation pushes belong to operational state, never the inbox.
    if (notification.type === 'event_snapshot' || notification.type === 'mutation_changed') return;
    // Check if notification type is enabled
    if (!notificationSettingsService.isNotificationEnabled(notification.type, preferences.value)) {
      return;
    }

    // Ensure notification has required fields
    const normalizedNotification = {
      id: notification.id || `notif_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      type: notification.type || 'system',
      title: notification.title || 'Notification',
      body: notification.body || notification.message || '',
      severity: notification.severity || 'info',
      timestamp: notification.timestamp || new Date().toISOString(),
      read: false,
      data: notification.data || {},
    };

    // Add to beginning of array
    notifications.value.unshift(normalizedNotification);

    // Keep only last 100 notifications in memory
    if (notifications.value.length > 100) {
      notifications.value = notifications.value.slice(0, 100);
    }

    // Save to cache
    saveToCacheThrottled();

    // Trigger new notification animation
    hasNewNotification.value = true;
    setTimeout(() => {
      hasNewNotification.value = false;
    }, 2000);

    // Show a toast for system notifications (respecting per-category prefs).
    // Suppress user-sourced echoes (api_mutation — you just made the change);
    // their detail lives in the panel, not a popup.
    const isUserSourced = getNotificationTab(normalizedNotification) === 'user';
    if (
      !isUserSourced &&
      notificationSettingsService.shouldShowToast(notification.type, preferences.value)
    ) {
      showToast(normalizedNotification);
    }

    return normalizedNotification;
  }

  function showToast(notification) {
    // No popup on pages where an unrelated toast is noise (the AI app consent page); the
    // notification still lands in the list.
    if (isToastSuppressedPath(window.location?.pathname)) return;

    const severityMap = {
      info: 'info',
      success: 'success',
      warning: 'warn',
      error: 'error',
    };

    const severity = severityMap[notification.severity] || 'info';

    window.dispatchEvent(
      new CustomEvent('showToast', {
        detail: {
          severity: severity,
          summary: notification.title,
          detail: notification.body,
          life: 5000,
          group: severity === 'error' || severity === 'warn' ? 'errors' : undefined,
        },
      }),
    );
  }

  function markAsRead(id) {
    const notification = notifications.value.find((n) => n.id === id);
    if (notification && !notification.read) {
      notification.read = true;
      notificationCacheService.updateNotification(id, { read: true });
    }
  }

  function markAllAsRead() {
    notifications.value.forEach((n) => {
      if (!n.read) {
        n.read = true;
      }
    });
    notificationCacheService.saveNotifications(notifications.value);
  }

  function removeNotification(id) {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index !== -1) {
      notifications.value.splice(index, 1);
      notificationCacheService.removeNotification(id);
    }
  }

  function clearAll() {
    notifications.value = [];
    notificationCacheService.clearCache();
  }

  function openDrawer() {
    isDrawerOpen.value = true;
  }

  function closeDrawer() {
    isDrawerOpen.value = false;
  }

  function toggleDrawer() {
    isDrawerOpen.value = !isDrawerOpen.value;
  }

  // WebSocket integration
  let unsubscribeConnectionState = null;
  let unsubscribeNotifications = null;
  let tokenUpdateHandler = null;
  let stopBusinessProfileWatcher = null;
  let socketContextVersion = 0;

  /**
   * Get the current access token for WebSocket connection
   * Uses the same business-profile-specific token as API calls
   */
  function getCurrentAccessToken() {
    const authStore = useAuthStore();
    return authStore.accessToken;
  }

  /**
   * Get the current marketplace profile ID (Amazon Ads profile ID)
   */
  function getCurrentProfileId() {
    const accountStore = useAccountStore();
    return accountStore.currentMarketplace?.profileId;
  }

  function initializeWebSocket({ inbox = false } = {}) {
    const authStore = useAuthStore();
    const accountStore = useAccountStore();

    // Subscribe to connection state changes
    if (!unsubscribeConnectionState) {
      unsubscribeConnectionState = webSocketService.onConnectionStateChange((state) => {
        connectionState.value = state;
      });
    }

    // The dev inbox is the only consumer of every message. Other callers listen
    // for their own types and must not fill the bell.
    if (inbox && !unsubscribeNotifications) {
      unsubscribeNotifications = webSocketService.subscribe('*', (notification) => {
        addNotification(notification);
      });
    }

    // App.vue can initialize from both mount and its login watcher.
    if (stopBusinessProfileWatcher) return;

    const selectionSettled = () =>
      accountStore.isDataLoaded &&
      !accountStore.isLoadingProfiles &&
      !accountStore.isChangingMarketplace;
    const businessProfileKey = () => {
      const id = accountStore.currentBusinessProfile?.id;
      return id == null ? null : String(id);
    };
    const marketplaceKey = () => getMarketplaceUid(accountStore.currentMarketplace);
    const profileKey = () => {
      const id = getCurrentProfileId();
      return id == null ? null : String(id);
    };
    const contextKey = () => JSON.stringify([businessProfileKey(), marketplaceKey(), profileKey()]);
    const credentials = async () => {
      const version = socketContextVersion;
      const context = contextKey();
      const bp = accountStore.currentBusinessProfile?.id;
      const profileId = getCurrentProfileId();
      if (!authStore.loggedIn || !bp || !selectionSettled()) return null;
      const token = await tokenService.getValidAppToken(bp);
      if (
        version !== socketContextVersion ||
        context !== contextKey() ||
        !authStore.loggedIn ||
        !selectionSettled() ||
        businessProfileIdFromToken(token) !== String(bp)
      ) {
        return null;
      }
      return { token, profileId };
    };
    webSocketService.setTokenProvider(credentials);

    let socketContext = null;
    let pendingConnection = null;
    const synchronizeSocket = async () => {
      const context = authStore.loggedIn && businessProfileKey() ? contextKey() : null;
      if (context !== socketContext) {
        socketContextVersion++;
        pendingConnection = null;
        // Retire the old tenant/profile immediately, but do not open its replacement
        // while the account store is still resolving the marketplace.
        if (connectionState.value !== CONNECTION_STATES.DISCONNECTED) webSocketService.disconnect();
        socketContext = context;
      }
      if (!context || !selectionSettled()) return;
      // Tokens authenticate a socket for its lifetime. Refreshes only wake a closed
      // socket; reconnect backoff already obtains fresh credentials via the provider.
      if (connectionState.value !== CONNECTION_STATES.DISCONNECTED || pendingConnection !== null) {
        return;
      }
      const version = ++socketContextVersion;
      pendingConnection = version;
      try {
        const result = await credentials();
        if (version === socketContextVersion && result) {
          webSocketService.connect(result.token, result.profileId);
        }
      } catch (error) {
        console.error('[NotificationStore] Unable to refresh socket credentials:', error);
      } finally {
        if (pendingConnection === version) pendingConnection = null;
      }
    };

    if (!tokenUpdateHandler) {
      tokenUpdateHandler = (event) => {
        if (String(event.detail?.businessProfileId) === String(accountStore.currentBusinessProfile?.id)) {
          synchronizeSocket();
        }
      };
      window.addEventListener('tokensUpdated', tokenUpdateHandler);
    }
    if (!stopBusinessProfileWatcher) {
      stopBusinessProfileWatcher = watch(
        [businessProfileKey, marketplaceKey, profileKey, () => authStore.loggedIn, selectionSettled],
        synchronizeSocket,
        { flush: 'sync' },
      );
    }
    synchronizeSocket();
  }

  function disconnectWebSocket() {
    socketContextVersion++;
    webSocketService.setTokenProvider(null);
    // Remove token update listener
    if (tokenUpdateHandler) {
      window.removeEventListener('tokensUpdated', tokenUpdateHandler);
      tokenUpdateHandler = null;
    }

    // Stop business profile watcher
    if (stopBusinessProfileWatcher) {
      stopBusinessProfileWatcher();
      stopBusinessProfileWatcher = null;
    }

    if (unsubscribeConnectionState) {
      unsubscribeConnectionState();
      unsubscribeConnectionState = null;
    }
    if (unsubscribeNotifications) {
      unsubscribeNotifications();
      unsubscribeNotifications = null;
    }
    // The singleton is reused after login; keep its visibility listener alive.
    webSocketService.disconnect();
  }

  function reconnectWebSocket(token, profileId) {
    const accessToken = token || getCurrentAccessToken();
    const profile = profileId || getCurrentProfileId();
    if (accessToken && profile) {
      webSocketService.reconnect(accessToken, profile);
    }
  }

  // Initialize
  async function initialize() {
    await loadPreferences();
    loadCachedNotifications();
  }

  // Expose for dev tools testing
  if (import.meta.env.DEV) {
    window.__notificationStore = {
      addNotification,
      notifications,
      connectionState,
      preferences,
      markAsRead,
      markAllAsRead,
      clearAll,
    };
  }

  return {
    // State
    notifications,
    connectionState,
    preferences,
    isDrawerOpen,
    hasNewNotification,

    // Computed
    unreadCount,
    unreadByCategory,
    sortedNotifications,

    // Actions
    initialize,
    loadPreferences,
    savePreferences,
    updatePreferences,
    resetPreferences,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    openDrawer,
    closeDrawer,
    toggleDrawer,

    // WebSocket
    initializeWebSocket,
    disconnectWebSocket,
    reconnectWebSocket,
  };
});
